/* eslint-disable no-console */
import { v4 as uuid } from 'uuid'
import { captureException, captureMessage } from '@sentry/nextjs'
import { differenceInWeeks } from 'date-fns'

import ROUTES from '~/shared/constants/routes.const'
import { callApi } from '~/services/axiosInstance.service'
import { Event, EventData, VideoWatchEvent } from '~/behavior-tracking/types'
import { getViewportId } from '~/behavior-tracking/utility/viewport-id.utility'
import viewEventsStore from '~/behavior-tracking/utility/view-events-store'
import { getPagePathOffset } from '~/behavior-tracking/utility/browser.utility'
import { getEventKey } from '~/behavior-tracking/utility/helpers'
import {
  getInvalidEventReason,
  getInvalidVideoEventReason,
} from '~/behavior-tracking/utility/stored-event-validation.utility'
import videoEventsStore from '~/behavior-tracking/utility/video-events-store'
import { checkViewKeyInBufferWithProduct } from '~/behavior-tracking/utility/checkViewKeyInBufferWithProduct'

import { MAX_PATH_LENGTH } from '../constants/tracking-constants'

const EVENT_SCHEMA_VERSION = '1'
const MAX_REMOVAL_PER_SESSION = 50
const EVENT_VALIDITY_WEEKS = 2
const MAX_SEND_BATCH_SIZE = 50

type EventStore = typeof viewEventsStore

interface StoredEvents<T> {
  valid: Array<{ key: string; event: T }>
  invalidKeys: string[]
  invalidReasons: string[]
}

function isExpired(now: Date, time: string): boolean {
  return differenceInWeeks(now, new Date(time)) >= EVENT_VALIDITY_WEEKS
}

function removeKeys(store: EventStore, keys: string[]): Promise<Array<PromiseSettledResult<void>>> {
  return Promise.allSettled(keys.map((key) => store.removeItem(key)))
}

async function readUserEvents<T>(
  store: EventStore,
  scopePrefix: string,
  getInvalidReason: (value: unknown) => string | null,
): Promise<StoredEvents<T>> {
  const storedEvents: StoredEvents<T> = { valid: [], invalidKeys: [], invalidReasons: [] }

  await store.iterate((value: unknown, key) => {
    if (!key.startsWith(scopePrefix)) {
      return
    }

    const invalidReason = getInvalidReason(value)
    if (invalidReason) {
      storedEvents.invalidKeys.push(key)
      storedEvents.invalidReasons.push(invalidReason)
      return
    }

    storedEvents.valid.push({ key, event: value as T })

    if (storedEvents.valid.length >= MAX_SEND_BATCH_SIZE) {
      return 'skipped-due-to-max-send-batch-size-reached'
    }
  })

  return storedEvents
}

async function findRemovableKeys(
  store: EventStore,
  getInvalidReason: (value: unknown) => string | null,
  getTime: (value: unknown) => string,
  now: Date,
  limit: number,
): Promise<string[]> {
  const keys: string[] = []

  if (limit < 1) {
    return keys
  }

  await store.iterate((value: unknown, key) => {
    if (getInvalidReason(value) || isExpired(now, getTime(value))) {
      keys.push(key)
    }

    if (keys.length >= limit) {
      return 'skipped-due-to-max-removal-received'
    }
  })

  return keys
}

async function submitEvents(events: Event[], videoEvents: VideoWatchEvent[]): Promise<void> {
  const response = await callApi({
    url: '/v1/user-stats/events',
    method: 'POST',
    data: { events, videoEvents },
  })

  if (response && 'error' in response) {
    const statusCode = response.meta?.response?.status ?? 'unknown'

    throw new Error(`Events submission failed with status code: ${statusCode}`)
  }
}

export async function logUserActivity(userId: number, eventData: EventData): Promise<void> {
  try {
    await createPageViewEvent(userId, eventData)
  } catch (err) {
    captureException(err, { tags: { feature: 'view-tracking', action: 'create-event' } })
    console.error('Failed to create activity log', err)
  }
}

async function createPageViewEvent(userId: number, eventData: EventData): Promise<void> {
  const pagePathOffset = getPagePathOffset()

  const event = {
    ...eventData,
    viewKey: eventData.viewKey.slice(0, MAX_PATH_LENGTH),
    extra: {
      ...(eventData.extra ?? {}),
      url: typeof document !== 'undefined' && document.URL.slice(pagePathOffset),
      pageVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
      schemaVersion: EVENT_SCHEMA_VERSION,
    },
    id: uuid(),
    time: new Date().toISOString(),
    viewportId: getViewportId(),
  }

  await viewEventsStore.setItem(getEventKey(userId, event.id), event)
}

export async function flushEvents(userId: number): Promise<
  | {
      eventsRelatedToVCC: boolean
      eventsRelatedToCurriculum: boolean
      eventsRelatedToVCCVideo: boolean
    }
  | undefined
> {
  const scopePrefix = `${userId}.`
  const viewEvents = await readUserEvents<Event>(viewEventsStore, scopePrefix, getInvalidEventReason)
  const videoEvents = await readUserEvents<VideoWatchEvent>(videoEventsStore, scopePrefix, getInvalidVideoEventReason)
  const droppedCount = viewEvents.invalidKeys.length + videoEvents.invalidKeys.length

  if (droppedCount > 0) {
    captureMessage('Dropped invalid stored tracking events', {
      level: 'warning',
      tags: { feature: 'view-tracking', action: 'drop-invalid-events' },
      extra: {
        droppedCount,
        viewEventsDropped: viewEvents.invalidKeys.length,
        videoEventsDropped: videoEvents.invalidKeys.length,
        reasons: [...new Set([...viewEvents.invalidReasons, ...videoEvents.invalidReasons])],
      },
    })

    // Not awaited: a failed delete must not block the send.
    void removeKeys(viewEventsStore, viewEvents.invalidKeys)
    void removeKeys(videoEventsStore, videoEvents.invalidKeys)
  }

  const eventsBuffer = viewEvents.valid.map(({ event }) => event)
  const videoEventsBuffer = videoEvents.valid.map(({ event }) => event)

  if (videoEventsBuffer.length < 1 && eventsBuffer.length < 1) {
    return
  }

  await submitEvents(eventsBuffer, videoEventsBuffer)

  const allEvents = [...eventsBuffer, ...videoEventsBuffer]

  const eventsRelatedToVCCVideo = checkViewKeyInBufferWithProduct(videoEventsBuffer, ROUTES.BOOTCAMP)
  const eventsRelatedToVCC = checkViewKeyInBufferWithProduct(allEvents, ROUTES.BOOTCAMP)
  const eventsRelatedToCurriculum = checkViewKeyInBufferWithProduct(allEvents, ROUTES.CURRICULUM)

  void removeKeys(
    viewEventsStore,
    viewEvents.valid.map(({ key }) => key),
  )
  void removeKeys(
    videoEventsStore,
    videoEvents.valid.map(({ key }) => key),
  )

  const returnObj = {
    eventsRelatedToVCC: false,
    eventsRelatedToCurriculum: false,
    eventsRelatedToVCCVideo: false,
  }

  if (eventsRelatedToVCC) {
    returnObj.eventsRelatedToVCC = true
  }

  if (eventsRelatedToCurriculum) {
    returnObj.eventsRelatedToCurriculum = true
  }

  if (eventsRelatedToVCCVideo) {
    returnObj.eventsRelatedToVCCVideo = true
  }

  return returnObj
}

export async function removedExpiredEvents(): Promise<void> {
  const now = new Date()
  const expiredEventKeys = await findRemovableKeys(
    viewEventsStore,
    getInvalidEventReason,
    (value) => (value as Event).time,
    now,
    MAX_REMOVAL_PER_SESSION,
  )
  const expiredVideoEventKeys = await findRemovableKeys(
    videoEventsStore,
    getInvalidVideoEventReason,
    (value) => (value as VideoWatchEvent).createdAt,
    now,
    MAX_REMOVAL_PER_SESSION - expiredEventKeys.length,
  )

  await Promise.all([
    removeKeys(viewEventsStore, expiredEventKeys),
    removeKeys(videoEventsStore, expiredVideoEventKeys),
  ])
}
